import { useState, useEffect, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { setDocumentTitle } from '../utils/translation'
import { FaSpinner, FaExclamationTriangle, FaCheckCircle, FaTimes, FaExternalLinkAlt } from 'react-icons/fa'

export default function GoogleCallback() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [status, setStatus] = useState<string>(t('auth.googleAuth.processing'))
  const [isError, setIsError] = useState<boolean>(false)
  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [showManualActions, setShowManualActions] = useState<boolean>(false)
  const [debugInfo, setDebugInfo] = useState<string[]>([])
  const [returnTo, setReturnTo] = useState<string>('/flowers')
  
  // Add debug logging
  const addDebugLog = useCallback((message: string) => {
    const timestamp = new Date().toLocaleTimeString()
    const logMessage = `[${timestamp}] ${message}`
    console.log(`[GoogleCallback] ${logMessage}`)
    setDebugInfo(prev => [...prev.slice(-4), logMessage]) // Keep last 5 logs
  }, [])

  // Manual close function
  const handleManualClose = useCallback(() => {
    addDebugLog('User clicked manual close')
    try {
      window.close()
    } catch (e) {
      addDebugLog(`Manual close failed: ${e}`)
      // If window.close() fails, try to navigate back to the main app
      window.location.href = returnTo
    }
  }, [addDebugLog, returnTo])

  // Try again function
  const handleTryAgain = useCallback(() => {
    addDebugLog('User clicked try again')
    window.location.href = returnTo
  }, [addDebugLog, returnTo])

  useEffect(() => {
    // Set document title
    setDocumentTitle(t('auth.googleAuth.title'))
    addDebugLog('GoogleCallback component mounted')
    
    // Show manual actions after 5 seconds
    const manualActionsTimer = setTimeout(() => {
      addDebugLog('5 seconds passed, showing manual actions')
      setShowManualActions(true)
    }, 5000)
    
    async function handleCallback() {
      try {
        const code = searchParams.get('code')
        const state = searchParams.get('state')
        const error = searchParams.get('error')
        
        addDebugLog(`Handling callback - code: ${code ? 'present' : 'missing'}, state: ${state ? 'present' : 'missing'}, error: ${error || 'none'}`)
        
        // Check for error from Google
        if (error) {
          const errorMsg = decodeURIComponent(error)
          addDebugLog(`Google OAuth error: ${errorMsg}`)
          setStatus(`${t('auth.googleAuth.authError')}: ${errorMsg}`)
          setIsError(true)
          sendMessageToOpener({ type: 'googleAuth', error: errorMsg })
          return
        }
        
        // Check if we have a code
        if (!code) {
          addDebugLog('Authorization code missing')
          setStatus(t('auth.googleAuth.authError') + ': ' + t('auth.googleAuth.errors.authorizationCodeMissing'))
          setIsError(true)
          sendMessageToOpener({ type: 'googleAuth', error: t('auth.googleAuth.errors.authorizationCodeMissing') })
          return
        }

        // Parse state parameter
        let stateReturnTo = '/flowers'
        let isRegistration = false
        try {
          if (state) {
            const stateObj = JSON.parse(decodeURIComponent(state))
            if (stateObj.returnTo) {
              stateReturnTo = stateObj.returnTo
            }
            if (stateObj.isRegistration) {
              isRegistration = stateObj.isRegistration
            }
          }
        } catch (e) {
          addDebugLog(`Error parsing state: ${e}`)
        }
        
        setReturnTo(stateReturnTo)
        addDebugLog(`Parsed state - returnTo: ${stateReturnTo}, isRegistration: ${isRegistration}`)

        // Exchange the code for token
        setStatus(t('auth.googleAuth.communicating'))
        addDebugLog('Starting token exchange')
        
        // Append registration flag to the request if needed
        let apiPath = '/api/auth/google-auth' + window.location.search
        if (isRegistration) {
          apiPath += (apiPath.includes('?') ? '&' : '?') + 'register=true'
        }
        
        addDebugLog(`Using API path: ${apiPath}`)
        const response = await fetch(apiPath)
        
        if (!response.ok) {
          const errorData = await response.json() as { error?: string }
          const errorMessage = errorData.error || t('auth.googleAuth.errors.authenticationFailed')
          addDebugLog(`API error: ${response.status} - ${errorMessage}`)
          setStatus(`${t('auth.googleAuth.authError')}: ${errorMessage}`)
          setIsError(true)
          sendMessageToOpener({ type: 'googleAuth', error: errorMessage })
          return
        }
        
        const data = await response.json() as { 
          token?: string,
          user?: Record<string, any>,
          session?: Record<string, any>
        }
        
        if (!data.token) {
          addDebugLog('Token missing from server response')
          setStatus(t('auth.googleAuth.authError') + ': ' + t('auth.googleAuth.errors.tokenMissing'))
          setIsError(true)
          sendMessageToOpener({ type: 'googleAuth', error: t('auth.googleAuth.errors.tokenMissing') })
          return
        }

        // Success! Send the token back to the opener window
        addDebugLog('Authentication successful, sending message to opener')
        setStatus(t('auth.googleAuth.success'))
        setIsSuccess(true)
        
        const messageData = { 
          type: 'googleAuth', 
          token: data.token,
          user: data.user,
          session: data.session,
          needsRefresh: true
        }
        
        // Check if this is a redirect flow (no opener window) vs popup flow
        const isRedirectFlow = !window.opener;
        
        if (isRedirectFlow) {
          // For redirect flow: store token and navigate directly
          addDebugLog('Redirect flow detected, storing token and navigating')
          localStorage.setItem('authToken', data.token);
          localStorage.setItem('lastActivityTime', Date.now().toString());
          
                     // Navigate to returnTo page after a short delay to show success message
           setTimeout(() => {
             addDebugLog(`${t('auth.googleAuth.errors.navigatingTo')}: ${stateReturnTo}`)
             window.location.href = stateReturnTo;
           }, 1500);
        } else {
          // For popup flow: send message to opener
          const messageSent = sendMessageToOpener(messageData)
          
          if (messageSent) {
            // Close this window after a short delay
            addDebugLog('Message sent successfully, attempting to close window')
            setTimeout(() => {
              try {
                window.close()
                addDebugLog('Window.close() called')
              } catch (e) {
                addDebugLog(`Window.close() failed: ${e}`)
                // If window.close() fails, show manual close option immediately
                setShowManualActions(true)
              }
            }, 1000)
          } else {
            addDebugLog('Failed to send message to opener, showing manual actions')
            setShowManualActions(true)
          }
        }
      } catch (error: any) {
        addDebugLog(`Unexpected error: ${error.message || error}`)
        console.error('Unexpected error during callback handling:', error)
        setStatus(`${t('auth.googleAuth.authError')}: ${error.message || t('auth.googleAuth.errors.unknownError')}`)
        setIsError(true)
        sendMessageToOpener({ type: 'googleAuth', error: error.message || t('auth.googleAuth.errors.unknownError') })
      }
    }
    
    // Helper function to send message to opener window
    function sendMessageToOpener(data: any): boolean {
      addDebugLog(`Attempting to send message to opener: ${JSON.stringify(data)}`)
      
      if (!window.opener) {
        addDebugLog('No opener window found')
        console.error('No opener window found')
        return false
      }
      
      if (window.opener.closed) {
        addDebugLog('Opener window is closed')
        console.error('Opener window is closed')
        return false
      }
      
      try {
        window.opener.postMessage(data, window.location.origin)
        addDebugLog('Message sent successfully to opener')
        return true
      } catch (e) {
        addDebugLog(`Failed to send message to opener: ${e}`)
        console.error('Failed to communicate with opener window:', e)
        return false
      }
    }
    
    handleCallback()
    
    // Cleanup timer on unmount
    return () => {
      clearTimeout(manualActionsTimer)
    }
  }, [searchParams, t, addDebugLog, returnTo])

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white p-6">
      <div className="w-full max-w-md text-center bg-white border border-gray-200 rounded-[16px] shadow-md p-8">
        {/* Main Status */}
        <div className="flex items-center justify-center mb-4">
          {isError ? (
            <FaExclamationTriangle className="text-4xl text-red-500 mr-3" />
          ) : isSuccess ? (
            <FaCheckCircle className="text-4xl text-green-500 mr-3" />
          ) : (
            <FaSpinner className="text-4xl text-[#F9D348] animate-spin mr-3" />
          )}
                     <h2 className="text-2xl font-semibold">
             {isError
               ? t('auth.googleAuth.authError')
               : t('auth.googleAuth.processing')}
           </h2>
        </div>
        
        <p className={`mb-6 ${isError ? 'text-red-600 font-medium' : isSuccess ? 'text-green-600 font-medium' : 'text-gray-700'}`}>
          {status}
        </p>
        
        {/* Manual Actions (shown after 5 seconds or on error) */}
        {(showManualActions || isError) && (
          <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                         <h3 className="text-lg font-medium mb-3 text-gray-800">
               {t('auth.googleAuth.windowStuck')}
             </h3>
             <p className="text-sm text-gray-600 mb-4">
               {t('auth.googleAuth.popupBlocked')}
             </p>
            
            <div className="space-y-3">
              <button
                onClick={handleManualClose}
                className="w-full flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                                 <FaTimes className="mr-2" />
                 {t('auth.googleAuth.manualClose')}
               </button>
               
               <button
                 onClick={handleTryAgain}
                 className="w-full flex items-center justify-center px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
               >
                 <FaExternalLinkAlt className="mr-2" />
                 {t('auth.googleAuth.tryAgain')}
               </button>
             </div>
             
             <p className="text-xs text-gray-500 mt-3">
               {t('auth.googleAuth.manualCloseDescription')}
             </p>
          </div>
        )}
        
        {/* Debug Information (only in development) */}
        {import.meta.env.DEV && debugInfo.length > 0 && (
          <details className="mt-6 text-left">
            <summary className="text-sm font-medium text-gray-600 cursor-pointer">
              Debug Information
            </summary>
            <div className="mt-2 p-3 bg-gray-100 rounded text-xs font-mono text-gray-800 max-h-32 overflow-y-auto">
              {debugInfo.map((log, index) => (
                <div key={index} className="mb-1">{log}</div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  )
} 